import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createLogger } from '@hunting-nature/shared-core/logging';
import { Store } from '@ngrx/store';
import { catchError, concat, concatMap, forkJoin, from, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs';

import { ImageApiService } from '../../../pages/gallery/image-gallery/services/image-api.service';
import { ImageProcessingService } from '../../../pages/gallery/image-gallery/services/image-processing.service';
import { ImageService } from '../../../pages/gallery/image-gallery/services/image.service';
import type { StorageInformation } from '../../../pages/menu/device-settings/storage-management/components/storage-information/storage-information';
import { StorageInformationService } from '../../../pages/menu/device-settings/storage-management/components/storage-information/storage-information.service';
import { BrowserService } from '../../services/browser.service';
import { HuntingProductMediaStore } from '../../services/hunting-product-media.store';
import { SettingsService } from '../../services/settings.service';
import { GeneralActions } from '../general/general.actions';
import { GeneralSelectors } from '../general/general.reducer';
import { HuntingProductSyncActions } from '../hunting-product-sync/hunting-product-sync.actions';

import { SettingsActions } from './settings.actions';

@Injectable()
export class SettingsEffects {
  // services
  private readonly actions$ = inject(Actions);
  private readonly storageInformationService = inject(StorageInformationService);
  private readonly cacheService = inject(ImageProcessingService);
  private readonly imageService = inject(ImageService);
  private readonly imageApiService = inject(ImageApiService);
  private readonly settingsService = inject(SettingsService);
  private readonly store = inject(Store);
  private readonly browserService = inject(BrowserService);
  private readonly huntingProductMediaStore = inject(HuntingProductMediaStore);
  private readonly log = createLogger('SettingsEffects');

  initStorageInformation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.initStorageInformation),
        switchMap(() =>
          this.storageInformationService.getStorageInformation$().pipe(
            map((storageInformation: StorageInformation) =>
              SettingsActions.initStorageInformationSucceeded({
                storageInformation,
              })
            ),
            catchError((error: unknown) => {
              this.log.error('getStorageInformation$ failed', { error });
              return of(SettingsActions.initStorageInformationFailed({ error }));
            })
          )
        )
      ),
    { dispatch: true }
  );

  deleteFullAppCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.deleteFullAppCache),
        switchMap(() =>
          from(this.cacheService.deleteAll$()).pipe(
            tap(() => this.imageApiService.clearDetailCache()),
            concatMap(() => of(SettingsActions.deleteFullAppCacheSucceeded(), SettingsActions.initStorageInformation())),
            catchError((error: unknown) => {
              this.log.error('deleteFullAppCache failed', { error });
              return of(SettingsActions.deleteFullAppCacheFailed({ error }));
            })
          )
        )
      ),
    { dispatch: true }
  );

  deleteHuntingProductMedia$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.deleteHuntingProductMedia),
        switchMap(() =>
          from(this.huntingProductMediaStore.deleteAll$()).pipe(
            map(() => SettingsActions.deleteHuntingProductMediaSucceeded()),
            catchError((error: unknown) => {
              this.log.error('deleteHuntingProductMedia failed', { error });
              return of(SettingsActions.deleteHuntingProductMediaFailed({ error }));
            })
          )
        )
      ),
    { dispatch: true }
  );

  deleteUserSettings$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.deleteUserSettings),
        switchMap(() => this.settingsService.clear$())
      ),
    { dispatch: false }
  );

  resetApplication$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.resetApplication),
        concatMap(() =>
          concat(
            // cancelSync first so no further item starts before the wipe; the item already in flight
            // cannot be stopped and may still land after it.
            of(
              HuntingProductSyncActions.cancelSync(),
              SettingsActions.deleteFullAppCache(),
              SettingsActions.deleteUserSettings(),
              SettingsActions.deleteHuntingProductMedia()
            ),
            // The downloaded captures are the only copy until synced — wait for the delete attempt to
            // finish (success or failure) before logout navigates away, or a pending write gets cut short.
            this.actions$.pipe(
              ofType(SettingsActions.deleteHuntingProductMediaSucceeded, SettingsActions.deleteHuntingProductMediaFailed),
              take(1),
              concatMap(() => of(HuntingProductSyncActions.localMediaCleared(), GeneralActions.logoutRequested()))
            )
          )
        )
      ),
    { dispatch: true }
  );

  showLegalDocument$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.showTermsAndConditions),
        withLatestFrom(this.store.select(GeneralSelectors.activeAppLanguage)),
        switchMap(([, lang]) => this.settingsService.fetchLegalDocuments$(lang).pipe(map(([document]) => document.documentUrl))),
        concatMap(pdf => of(SettingsActions.fetchConsentSucceeded({ pdf }))),
        catchError(() => of(SettingsActions.fetchConsentFailed()))
      ),
    { dispatch: true }
  );

  openPdfDocument$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SettingsActions.fetchConsentSucceeded),
        switchMap(action => this.browserService.openExternalUrl(action.pdf))
      ),
    { dispatch: false }
  );
}
